/**
 * Ce i se arată unui vizitator al clinicii demonstrative, ales de el înainte
 * să intre.
 *
 * ── De ce trăiește în browser și nu în baza de date ─────────────────────
 * Demo-ul e o singură clinică, publică, pe care o poate edita oricine care
 * are linkul (`isPublicDemo`). Alegerea unui vizitator nu poate deci schimba
 * setările clinicii: al doilea vizitator ar vedea ce a ales primul, iar al
 * treilea n-ar înțelege de ce lipsește Stocuri. Așa că nu se scrie nimic pe
 * server — alegerea stă în browserul celui care a făcut-o, ascunde secțiuni
 * din meniu, și dispare odată cu el.
 *
 * ── Ce nu face, deliberat ───────────────────────────────────────────────
 * Nu oprește nimic pe server și nu atinge `org_config.modules`: API-ul
 * răspunde în continuare la tot, iar Setări → Module arată produsul întreg,
 * pentru că aia *este* configurația clinicii demo. Eticheta din bara de sus e
 * cea care explică diferența — „Vedere: Cabinet solo · schimbă". Fără ea, un
 * vizitator care a ascuns Stocuri n-ar avea drum înapoi și ar citi demo-ul ca
 * pe o aplicație stricată, nu ca pe una filtrată.
 *
 * Distincția contează: asta e o vedere, nu o configurație. Un ecran care ar
 * pretinde altceva — „am configurat demo-ul pentru tine" — ar fi exact genul
 * de lucru pe care aplicația îl declară și nu îl face, pe care restul
 * verificărilor din repo îl vânează.
 *
 * ── Axa ─────────────────────────────────────────────────────────────────
 * Cine lucrează în cabinet, nu ce specialitate are. Specialitatea schimbă
 * *datele* (servicii, planuri, dinți), iar datele demo-ului sunt aceleași
 * pentru toți vizitatorii; câți oameni sunt schimbă *ecranele*, care e fix ce
 * poate face o vedere din browser.
 */

/** Cheile din `MODULE_CATALOGUE` pe care le poate ascunde o vedere. */
export interface DemoView {
  key: string;
  /**
   * Cheia i18n a numelui, scrisă cu tot cu spațiul de nume.
   *
   * `common:` pentru că se citește din două locuri cu `t`-uri diferite —
   * pagina de login e pe `auth`, bara de sus pe `common` — iar o cheie
   * calificată se rezolvă din oricare.
   */
  labelKey: string;
  /** Cheia i18n a explicației de sub nume, la fel calificată. */
  hintKey: string;
  /**
   * Modulele lăsate în afara meniului.
   *
   * Cheile sunt ale catalogului de module al API-ului
   * (`feature-catalogue.ts`); `npm run check:modules` refuză una care nu
   * există acolo, ca o redenumire să nu lase în urmă o vedere care nu mai
   * ascunde nimic.
   */
  hides: readonly string[];
  /** Contul demo cu care are sens să înceapă genul ăsta de cabinet. */
  suggests: { role?: string; kind?: 'staff' | 'patient' };
}

export const DEMO_VIEWS: readonly DemoView[] = [
  {
    key: 'solo',
    labelKey: 'common:demoVedereSolo',
    hintKey: 'common:demoVedereSoloHint',
    // Fără personal de angajat, fără laborator propriu, fără chat de echipă —
    // într-un cabinet de un om toate trei sunt ecrane goale. Imagistica și
    // rapoartele rămân: alea sunt motivul pentru care se uită.
    hides: ['hr', 'dentalLab', 'teamChat', 'economy'],
    suggests: { role: 'medic' },
  },
  {
    key: 'receptie',
    labelKey: 'common:demoVedereReceptie',
    hintKey: 'common:demoVedereReceptieHint',
    hides: ['hr', 'dentalLab'],
    suggests: { role: 'receptie' },
  },
  {
    key: 'clinica',
    labelKey: 'common:demoVedereClinica',
    hintKey: 'common:demoVedereClinicaHint',
    hides: [],
    suggests: { role: 'admin' },
  },
] as const;

export function demoViewByKey(key: string | null | undefined): DemoView | null {
  if (!key) return null;
  return DEMO_VIEWS.find((v) => v.key === key) ?? null;
}

/**
 * Ascunde vederea asta modulul cerut?
 *
 * O cheie necunoscută nu ascunde nimic: un modul adăugat mâine se vede în
 * toate vederile până când cineva decide altfel, exact ca la `hasFeature`.
 */
export function demoViewHides(view: DemoView | null, feature: string): boolean {
  return view ? view.hides.includes(feature) : false;
}

const STORAGE_KEY = 'df.demoView';

/**
 * Alegerea din browserul ăsta, dacă mai înseamnă ceva.
 *
 * Se validează pe catalog la citire: o cheie rămasă de la o vedere care nu mai
 * există ar ascunde pe veci un modul pe care nimeni nu l-a ales să fie ascuns.
 * Citirea poate să și arunce (fereastră privată, date de sit blocate), deci
 * totul e într-un `try` — fără alegere e starea normală, nu o eroare.
 */
export function readDemoView(): DemoView | null {
  try {
    return demoViewByKey(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function writeDemoView(key: string | null): void {
  try {
    if (key) window.localStorage.setItem(STORAGE_KEY, key);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Un browser care nu ne lasă să ținem minte alegerea arată produsul
    // întreg. Asta e o vedere validă, nu un motiv de eroare pe ecran.
  }
}

/** Contul demo pe care vederea asta îl recomandă, dintre cele publicate. */
export function suggestedAccount<T extends { role?: string | null; kind?: string }>(
  view: DemoView | null,
  accounts: readonly T[],
): T | null {
  if (!view) return null;
  const { role, kind } = view.suggests;
  return (
    accounts.find((a) => (role ? a.role === role : true) && (kind ? a.kind === kind : true)) ?? null
  );
}
